// BrimLight/client/ui/ProjectSelector.js
import { ComponentBase } from "../core/ComponentBase.js";

export class ProjectSelector extends ComponentBase {
    static styles = `
        /* Main button in the header */
        .project-selector-button {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            background-color: var(--bs-tertiary-bg);
            border: 1px solid var(--bs-border-color-translucent);
            border-radius: var(--bs-border-radius);
            padding: 0.3rem 0.6rem;
            cursor: pointer;
            transition: background-color 0.2s ease;
        }
        .project-selector-button:hover {
            background-color: var(--bs-secondary-bg);
        }
        .project-selector-button .project-name {
            font-weight: 500;
        }
        .project-selector-button i {
            color: var(--bs-secondary-color);
        }

        /* Modal styling */
        .project-selector-modal .modal-dialog {
            max-width: 600px;
        }
        .project-selector-modal .modal-body {
            min-height: 400px;
            max-height: 70vh;
            padding: 0;
            display: flex;
            flex-direction: column;
        }
        .project-list-container {
            flex-grow: 1;
            overflow-y: auto;
            padding: 0 1rem;
        }

        /* List item styling */
        .project-item {
            display: flex;
            align-items: center;
            padding: 0.75rem 1rem;
            border-bottom: 1px solid var(--bs-border-color-translucent);
            cursor: pointer;
            gap: 1rem;
        }
        .project-item:last-child {
            border-bottom: none;
        }
        .project-item:hover {
            background-color: var(--bs-tertiary-bg);
        }
        .project-item-details {
            flex-grow: 1;
            overflow: hidden;
        }
        .project-item-name {
            font-weight: 500;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .project-item-id {
            font-size: 0.8rem;
            color: var(--bs-secondary-color);
        }
        .project-item-actions .dropdown-toggle::after {
            display: none;
        }

        /* --- MOBILE FIRST STYLES --- */
        @media (max-width: 767.98px) {
            .project-selector-button .project-name,
            .project-selector-button .bi-chevron-down {
                display: none; /* Hide text and chevron on mobile */
            }
            .project-selector-button {
                padding: 0.3rem 0.5rem; /* Make button more compact */
            }
            /* Make modal full screen on mobile */
            .project-selector-modal .modal-dialog {
                margin: 0;
                max-width: 100%;
                height: 100%;
            }
            .project-selector-modal .modal-content {
                height: 100%;
                border-radius: 0;
                border: none;
            }
            .project-selector-modal .modal-body {
                max-height: none; /* Allow body to fill space */
            }
        }
    `;

    constructor(config) {
        super(config);
        this.projects = [];
        this.recentProjects = [];
        this.filteredProjects = [];
        this.selectedProject = null;
        this.searchTerm = '';
        this.activeTab = 'recent';

        this.modal = null;
        this.modalInstance = null;

        this.storageKey = 'brimlight_selectedProject';
        this.recentStorageKey = 'brimlight_recentProjects';

        this._boundHandleItemClick = this.handleItemClick.bind(this);
        this._boundHandleTabClick = this.handleTabClick.bind(this);
        this._boundSearchProjects = this.searchProjects.bind(this);
        this._boundCreateProject = () => this.showProjectModal('add');

        // Specific handlers for server events
        this._boundHandleProjectCreated = this.handleProjectCreated.bind(this);
        this._boundHandleProjectUpdated = this.handleProjectUpdated.bind(this);
        this._boundHandleProjectDeleted = this.handleProjectDeleted.bind(this);

        // --- FIX: Initialize the handler for the UI event ---
        this._boundShowCreateModal = () => this.showProjectModal('add');

        this.applyStyles();
    }

    async initialize() {
        this.appClient.events.server.on('projectCreated', this._boundHandleProjectCreated);
        this.appClient.events.server.on('projectUpdated', this._boundHandleProjectUpdated);
        this.appClient.events.server.on('projectDeleted', this._boundHandleProjectDeleted);
        
        // --- FIX: Listen for the UI event ---
        this.appClient.events.local.on('ui:show-project-modal', this._boundShowCreateModal);
        
        await super.initialize();
    }

    async initAfterLogin() {
        await this.loadProjectsAndSetup();
    }

    async handleProjectCreated(newProjectData) {
        this.log.info(`Received server event: projectCreated`, newProjectData);
        await this.loadProjectsAndSetup();
        const projectToSelect = this.projects.find(p => p._id === newProjectData._id);
        if (projectToSelect) {
            this.selectProject(projectToSelect);
            this.appClient.notifier.success(`Project "${projectToSelect.name}" created and selected.`);
        }
    }

    async handleProjectUpdated(updatedProjectData) {
        this.log.info(`Received server event: projectUpdated`, updatedProjectData);
        await this.loadProjectsAndSetup();
    }

    async handleProjectDeleted({ projectId }) {
        this.log.info(`Received server event: projectDeleted`, { projectId });
        const wasSelected = this.selectedProject?._id === projectId;
        await this.loadProjectsAndSetup();
        if (wasSelected) {
            this.selectProject(null);
            this.appClient.notifier.info("The currently selected project has been deleted.");
        }
    }

    async loadProjectsAndSetup() {
        try {
            this.projects = await this.appClient.rpcClient.call('VideoProjectRpc.listProjects');
            this.loadRecentProjects();
            this.restoreSelectedProject();

            if (this.modal && this.modalInstance._isShown) {
                this.filterAndRenderProjects();
            }
        } catch (error) {
            this.log.error("Error loading projects:", error);
            this.appClient.notifier.error("Failed to load projects.");
        }
    }

    restoreSelectedProject() {
        const storedProjectId = localStorage.getItem(this.storageKey);
        const project = this.projects.find(p => p._id === storedProjectId);

        if (storedProjectId && !project) {
            this.selectProject(null, true);
        } else {
            this.selectProject(project, true);
        }

        if (this.container) this.render();
    }

    loadRecentProjects() {
        const recentIds = JSON.parse(localStorage.getItem(this.recentStorageKey) || '[]');
        this.recentProjects = recentIds
            .map(id => this.projects.find(p => p._id === id))
            .filter(Boolean);
    }

    addToRecent(projectId) {
        let recentIds = JSON.parse(localStorage.getItem(this.recentStorageKey) || '[]');
        recentIds = [projectId, ...recentIds.filter(id => id !== projectId)];
        localStorage.setItem(this.recentStorageKey, JSON.stringify(recentIds.slice(0, 7)));
        this.loadRecentProjects();
    }

    selectProject(project, fireEvent = true) {
        this.selectedProject = project;
        if (project) {
            localStorage.setItem(this.storageKey, project._id);
            this.addToRecent(project._id);
        } else {
            localStorage.removeItem(this.storageKey);
        }

        this.appClient.setSharedData('projectId', this.selectedProject?._id);
        this.appClient.setSharedData('selectedProject', this.selectedProject);
        this.appClient.setSharedData('selectedProjectId', this.selectedProject?._id);

        if (fireEvent) {
            this.appClient.events.fireLocal('projectSelected', { projectData: this.selectedProject });
        }

        if (this.modalInstance && this.modalInstance._isShown) this.modalInstance.hide();
        if (this.container) this.render();
    }

    render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'project-selector-component';
            this.container.addEventListener('click', (e) => {
                if (!e.target.closest('.project-selector-button')) return;
                this.show();
            });
        }

        let buttonContent = '';
        if (this.selectedProject) {
            buttonContent = `<i class="bi bi-folder2-open"></i> <span class="project-name">${this.selectedProject.name}</span> <i class="bi bi-chevron-down"></i>`;
        } else {
            buttonContent = `<i class="bi bi-folder"></i> <span class="project-name text-muted" data-i18n="Select Project">Select Project</span> <i class="bi bi-chevron-down"></i>`;
        }
        this.container.innerHTML = `<div class="project-selector-button">${buttonContent}</div>`;
        this.appClient.tx.applyTranslations(this.container);
        return this.container;
    }

    show() {
        if (!this.modal) {
            this.renderModal();
        }
        this.filterAndRenderProjects();
        this.modalInstance.show();
    }

    renderModal() {
        this.modal = document.createElement('div');
        this.modal.className = 'modal fade project-selector-modal';
        this.modal.id = `${this.config.instanceId}-modal`;
        this.modal.setAttribute('tabindex', '-1');
        this.modal.innerHTML = `
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" data-i18n="Select a Project">Select a Project</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                        <div class="p-3 border-bottom"><input type="search" class="form-control" id="${this.config.instanceId}-search" placeholder="Search projects..."></div>
                        <ul class="nav nav-tabs nav-fill px-3" role="tablist">
                            <li class="nav-item" role="presentation"><button class="nav-link active" data-tab="recent" type="button" role="tab">Recent</button></li>
                            <li class="nav-item" role="presentation"><button class="nav-link" data-tab="all" type="button" role="tab">All</button></li>
                        </ul>
                        <div class="project-list-container list-group list-group-flush"></div>
                    </div>
                    <div class="modal-footer"><button type="button" class="btn btn-primary" id="${this.config.instanceId}-new-project-btn"><i class="bi bi-plus-lg me-2"></i> New Project</button></div>
                </div>
            </div>`;
        document.body.appendChild(this.modal);
        this.modalInstance = new bootstrap.Modal(this.modal);

        this.modal.querySelector(`#${this.config.instanceId}-search`).addEventListener('input', this._boundSearchProjects);
        this.modal.querySelectorAll('.nav-link[data-tab]').forEach(tab => tab.addEventListener('click', this._boundHandleTabClick));
        this.modal.querySelector(`#${this.config.instanceId}-new-project-btn`).addEventListener('click', this._boundCreateProject);
        this.modal.querySelector('.project-list-container').addEventListener('click', this._boundHandleItemClick);
    }

    handleTabClick(event) {
        this.activeTab = event.target.dataset.tab;
        this.modal.querySelectorAll('.nav-link[data-tab]').forEach(tab => tab.classList.remove('active'));
        event.target.classList.add('active');
        this.filterAndRenderProjects();
    }

    searchProjects(event) {
        this.searchTerm = event.target.value.toLowerCase();
        this.filterAndRenderProjects();
    }

    filterAndRenderProjects() {
        const sourceList = this.activeTab === 'recent' ? this.recentProjects : this.projects;
        this.filteredProjects = this.searchTerm
            ? sourceList.filter(p => p.name.toLowerCase().includes(this.searchTerm))
            : sourceList;
        this.renderProjectList();
    }

    renderProjectList() {
        const listContainer = this.modal.querySelector('.project-list-container');
        listContainer.innerHTML = '';
        if (this.filteredProjects.length === 0) {
            listContainer.innerHTML = `<div class="text-center text-muted p-5">No projects found.</div>`; return;
        }
        this.filteredProjects.forEach(project => {
            const dropdownHtml = `
                <div class="dropdown">
                    <button class="btn btn-sm btn-link text-secondary" type="button" data-bs-toggle="dropdown" data-action="toggle-menu"><i class="bi bi-three-dots-vertical"></i></button>
                    <ul class="dropdown-menu dropdown-menu-end">
                        <li><a class="dropdown-item" href="#" data-action="edit"><i class="bi bi-pencil-square me-2"></i>Edit</a></li>
                        <li><a class="dropdown-item text-danger" href="#" data-action="delete"><i class="bi bi-trash me-2"></i>Delete</a></li>
                    </ul>
                </div>`;
            const item = document.createElement('div');
            item.className = 'project-item';
            item.dataset.projectId = project._id;
            item.innerHTML = `<i class="bi bi-film fs-4 text-primary"></i><div class="project-item-details"><div class="project-item-name">${project.name}</div><div class="project-item-id">ID: ${project._id}</div></div><div class="project-item-actions ms-auto">${dropdownHtml}</div>`;
            listContainer.appendChild(item);
        });
    }

    handleItemClick(event) {
        const projectItem = event.target.closest('.project-item');
        if (!projectItem) return;

        const actionTarget = event.target.closest('[data-action]');
        const projectId = projectItem.dataset.projectId;
        const project = this.projects.find(p => p._id === projectId);

        if (actionTarget) {
            event.stopPropagation();
            const action = actionTarget.dataset.action;
            if (action === 'toggle-menu') return;
            if (action === 'edit') this.showProjectModal('edit', project);
            if (action === 'delete') this.handleDeleteProject(projectId);
        } else {
            this.selectProject(project);
        }
    }

    async handleDeleteProject(projectId) {
        const project = this.projects.find(p => p._id === projectId);
        if (!project) return;
        const confirmed = await this.appClient.notifier.confirm(`Delete project "${project.name}"? This cannot be undone.`, { type: 'danger' });
        if (!confirmed) return;
        try {
            await this.appClient.rpcClient.call('VideoProjectRpc.deleteProject', { projectId });
            this.appClient.notifier.success(`Project "${project.name}" deleted.`);
        } catch (error) { this.appClient.notifier.error(error.message); }
    }

    showProjectModal(mode, projectData = null) {
        if (this.modalInstance) this.modalInstance.hide();
        const isEditMode = mode === 'edit';
        const title = isEditMode ? 'Edit Project' : 'Create New Project';

        this.createManagedComponent("DataForm", null, {
            instanceId: 'project-form-modal',
            layout: 'dialog',
            title: title,
            schemaPath: "collections/videoProjects",
            fields: {
                name: { type: 'text', label: 'Project Name', required: true },
                seriesId: {
                    type: 'select',
                    label: 'Associate with Series (Optional)',
                    optionsSourceRpc: {
                        method: 'SeriesRpc.getFieldValues',
                    },
                },
                initialPrompt: { type: 'textarea', label: 'What is your video about?', rows: 4, placeholder: 'e.g., A short promotional video for a new coffee shop.' },
            },
            actions: { save: true, cancel: true }
        }).then(form => {
            if (isEditMode && projectData) form.setData(projectData);

            form.on('dataSaved', async (formData) => {
                try {
                    const newProject = await this.appClient.rpcClient.call('VideoProjectRpc.createProject', formData);
                    this.appClient.notifier.success(`Project "${newProject.name}" created.`);
                } catch (err) { this.appClient.notifier.error(err.message); }
                finally { form.destroy(); }
            });

            form.on('dataUpdated', async (updatedData) => {
                try {
                    const payload = { projectId: projectData._id, ...updatedData };
                    await this.appClient.rpcClient.call('VideoProjectRpc.updateProject', payload);
                    this.appClient.notifier.success(`Project "${projectData.name}" updated.`);
                } catch (err) { this.appClient.notifier.error(err.message); }
                finally { form.destroy(); this.show(); }
            });

            form.on('cancel', () => { 
                form.destroy(); 
                // Only re-show the selector modal if we weren't editing (i.e., we cancelled creation)
                // or if we want to go back to the list.
                if (!isEditMode) this.show(); 
            });
            form.show();
        });
    }

    destroy() {
        this.appClient.events.server.off('projectCreated', this._boundHandleProjectCreated);
        this.appClient.events.server.off('projectUpdated', this._boundHandleProjectUpdated);
        this.appClient.events.server.off('projectDeleted', this._boundHandleProjectDeleted);
        
        // --- FIX: Cleanup the UI event listener ---
        this.appClient.events.local.off('ui:show-project-modal', this._boundShowCreateModal);

        this.modal?.querySelector('.project-list-container')?.removeEventListener('click', this._boundHandleItemClick);
        this.modal?.querySelector(`#${this.config.instanceId}-search`)?.removeEventListener('input', this._boundSearchProjects);
        this.modal?.querySelectorAll('.nav-link[data-tab]').forEach(tab => tab.removeEventListener('click', this._boundHandleTabClick));
        this.modal?.querySelector(`#${this.config.instanceId}-new-project-btn`)?.removeEventListener('click', this._boundCreateProject);

        this.modalInstance?.dispose();
        this.modal?.remove();
        super.destroy();
    }
}